import React, { useMemo, useState } from "react";
import { TEMPLATE_COLUMNS } from "./reportColumnCatalog";

const styles = {
  container: {
    display: "grid",
    gridTemplateColumns: "1fr auto 1fr",
    gap: "16px",
    alignItems: "stretch",
    marginTop: "16px",
  },
  panel: {
    border: "1px solid #e2e8f0",
    borderRadius: "8px",
    display: "flex",
    flexDirection: "column",
    height: "600px",
    backgroundColor: "#fff",
    overflow: "hidden",
  },
  panelHeader: {
    padding: "12px 16px",
    borderBottom: "1px solid #e2e8f0",
    backgroundColor: "#f8fafc",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  searchWrap: {
    padding: "8px 16px",
    borderBottom: "1px solid #e2e8f0",
    backgroundColor: "#fff",
  },
  list: {
    flex: 1,
    overflowY: "auto",
    padding: "8px",
    minHeight: "200px",
  },
  centerActions: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    gap: "12px",
  },
  accordionHeader: {
    display: "flex",
    alignItems: "center",
    padding: "8px 12px",
    backgroundColor: "#f1f5f9",
    borderRadius: "4px",
    cursor: "pointer",
    fontWeight: "600",
    fontSize: "13px",
    color: "#334155",
    marginBottom: "4px",
    marginTop: "8px",
  },
  accordionContent: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    paddingLeft: "8px",
  },
  row: {
    display: "flex",
    alignItems: "center",
    padding: "6px 8px",
    borderRadius: "4px",
    backgroundColor: "#fff",
    cursor: "grab",
    border: "1px solid transparent",
    transition: "opacity 0.2s",
  },
  selectedRow: {
    display: "flex",
    alignItems: "center",
    padding: "6px 8px",
    borderRadius: "4px",
    backgroundColor: "#fff",
    border: "1px solid #e2e8f0",
    marginBottom: "6px",
    cursor: "grab",
    transition: "opacity 0.2s",
  },
  checkbox: {
    marginRight: "10px",
    cursor: "pointer",
  },
  labelInput: {
    flex: 1,
    minWidth: "100px",
    padding: "4px 8px",
    border: "1px solid #cbd5e1",
    borderRadius: "4px",
    fontSize: "13px",
  },
  iconBtn: {
    background: "transparent",
    border: "none",
    padding: "4px",
    color: "#64748b",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },
};

export default function ReportTemplateColumnsEditor({
  columnOrder = [],
  visibleColumns = {},
  columnLabels = {},
  defaultVisible = {},
  defaultOrder = [],
  onChange,
}) {
  const [searchQuery, setSearchQuery] = useState("");
  const [checkedAvailable, setCheckedAvailable] = useState(new Set());
  const [checkedSelected, setCheckedSelected] = useState(new Set());
  const [expandedGroups, setExpandedGroups] = useState({});
  
  const [dragOverKey, setDragOverKey] = useState(null);
  const [dropPosition, setDropPosition] = useState("top");
  const [draggedKey, setDraggedKey] = useState(null);

  // ================= DATA PREPARATION =================
  const availableColumns = useMemo(() => {
    return TEMPLATE_COLUMNS.filter((c) => visibleColumns[c.viewKey] === false);
  }, [visibleColumns]);

  const selectedColumns = useMemo(() => {
    return TEMPLATE_COLUMNS.filter((c) => visibleColumns[c.viewKey] !== false)
      .sort((a, b) => {
        const idxA = columnOrder.indexOf(a.viewKey);
        const idxB = columnOrder.indexOf(b.viewKey);
        if (idxA === -1 && idxB === -1) return 0;
        if (idxA === -1) return 1;
        if (idxB === -1) return -1;
        return idxA - idxB;
      });
  }, [visibleColumns, columnOrder]);

  const q = searchQuery.toLowerCase();
  const filteredAvailable = useMemo(() => {
    return availableColumns.filter(
      (c) => c.label.toLowerCase().includes(q) || c.group.toLowerCase().includes(q)
    );
  }, [availableColumns, q]);

  const filteredSelected = useMemo(() => {
    return selectedColumns.filter((c) =>
      (columnLabels[c.viewKey] || c.label).toLowerCase().includes(q)
    );
  }, [selectedColumns, columnLabels, q]);

  const groupedAvailable = useMemo(() => {
    const groups = {};
    for (const col of filteredAvailable) {
      if (!groups[col.group]) groups[col.group] = [];
      groups[col.group].push(col);
    }
    return groups;
  }, [filteredAvailable]);

  // ================= HANDLERS =================
  const emit = (next) => {
    onChange?.({
      columnOrder: next.columnOrder ?? columnOrder,
      visibleColumns: next.visibleColumns ?? visibleColumns,
      columnLabels: next.columnLabels ?? columnLabels,
    });
  };

  const toggleAvailableCheck = (viewKey) => {
    const next = new Set(checkedAvailable);
    if (next.has(viewKey)) next.delete(viewKey);
    else next.add(viewKey);
    setCheckedAvailable(next);
  };

  const toggleSelectedCheck = (viewKey) => {
    const next = new Set(checkedSelected);
    if (next.has(viewKey)) next.delete(viewKey);
    else next.add(viewKey);
    setCheckedSelected(next);
  };

  const handleSelectAllAvailable = (e) => {
    if (e.target.checked) setCheckedAvailable(new Set(filteredAvailable.map((c) => c.viewKey)));
    else setCheckedAvailable(new Set());
  };

  const handleSelectAllSelected = (e) => {
    if (e.target.checked) setCheckedSelected(new Set(filteredSelected.map((c) => c.viewKey)));
    else setCheckedSelected(new Set());
  };

  const moveRight = () => {
    if (checkedAvailable.size === 0) return;
    const newVisible = { ...visibleColumns };
    const newOrder = [...columnOrder];

    checkedAvailable.forEach((key) => {
      newVisible[key] = true;
      if (!newOrder.includes(key)) newOrder.push(key);
    });

    emit({ visibleColumns: newVisible, columnOrder: newOrder });
    setCheckedAvailable(new Set());
  };

  const moveLeft = () => {
    if (checkedSelected.size === 0) return;
    const newVisible = { ...visibleColumns };
    checkedSelected.forEach((key) => {
      newVisible[key] = false;
    });

    emit({ visibleColumns: newVisible });
    setCheckedSelected(new Set());
  };

  const setLabel = (viewKey, val) => {
    emit({ columnLabels: { ...columnLabels, [viewKey]: val } });
  };

  const toggleGroup = (group) => {
    setExpandedGroups((prev) => ({ ...prev, [group]: !prev[group] }));
  };

  const moveRow = (viewKey, dir) => {
    const current = [...selectedColumns];
    const idx = current.findIndex((c) => c.viewKey === viewKey);
    const targetIdx = dir === "up" ? idx - 1 : idx + 1;
    if (targetIdx < 0 || targetIdx >= current.length) return;

    const [moved] = current.splice(idx, 1);
    current.splice(targetIdx, 0, moved);
    emit({ columnOrder: current.map((c) => c.viewKey) });
  };

  const handleReset = () => {
    emit({
      visibleColumns: { ...defaultVisible },
      columnOrder: [...defaultOrder],
      columnLabels: {},
    });
    setCheckedAvailable(new Set());
    setCheckedSelected(new Set());
  };

  // ================= DRAG & DROP LOGIC =================
  const onDragStart = (e, viewKey) => {
    e.dataTransfer.setData("text/plain", viewKey);
    e.dataTransfer.effectAllowed = "move";
    setDraggedKey(viewKey);
  };

  const onDragEnd = () => {
    setDraggedKey(null);
    setDragOverKey(null);
  };

  const onDragOverItem = (e, viewKey) => {
    e.preventDefault();
    e.stopPropagation();
    
    const rect = e.currentTarget.getBoundingClientRect();
    const offset = e.clientY - rect.top;
    const isBottomHalf = offset > rect.height / 2;

    setDragOverKey(viewKey);
    setDropPosition(isBottomHalf ? "bottom" : "top");
  };

  const onDropLeftPanel = (e) => {
    e.preventDefault();
    setDragOverKey(null);
    const sourceKey = e.dataTransfer.getData("text/plain");
    if (!sourceKey) return;

    if (visibleColumns[sourceKey] !== false) {
      emit({ visibleColumns: { ...visibleColumns, [sourceKey]: false } });
    }
    setDraggedKey(null);
  };

  const onDropRightItem = (e, targetKey) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverKey(null);
    
    const sourceKey = e.dataTransfer.getData("text/plain");
    if (!sourceKey || sourceKey === targetKey) return;

    const isFromLeft = visibleColumns[sourceKey] === false;
    
    let currentKeys = [...selectedColumns.map(c => c.viewKey)];
    const targetIdx = currentKeys.indexOf(targetKey);
    
    let insertIdx = targetIdx;
    if (dropPosition === "bottom") insertIdx += 1;

    if (isFromLeft) {
       const newVisible = { ...visibleColumns, [sourceKey]: true };
       currentKeys.splice(insertIdx, 0, sourceKey);
       
       const unselectedKeys = columnOrder.filter(k => visibleColumns[k] === false);
       emit({ visibleColumns: newVisible, columnOrder: [...currentKeys, ...unselectedKeys] });
    } else {
       const sourceIdx = currentKeys.indexOf(sourceKey);
       currentKeys.splice(sourceIdx, 1);
       
       let adjustedInsertIdx = insertIdx;
       if (sourceIdx < insertIdx) adjustedInsertIdx -= 1;
       
       currentKeys.splice(adjustedInsertIdx, 0, sourceKey);
       
       const unselectedKeys = columnOrder.filter(k => visibleColumns[k] === false);
       emit({ columnOrder: [...currentKeys, ...unselectedKeys] });
    }
    setDraggedKey(null);
  };

  const onDropRightPanelEmpty = (e) => {
    e.preventDefault();
    setDragOverKey(null);
    const sourceKey = e.dataTransfer.getData("text/plain");
    if (!sourceKey) return;

    const isFromLeft = visibleColumns[sourceKey] === false;
    let currentKeys = [...selectedColumns.map(c => c.viewKey)];
    
    if (isFromLeft) {
      const newVisible = { ...visibleColumns, [sourceKey]: true };
      currentKeys.push(sourceKey); 
      const unselectedKeys = columnOrder.filter(k => visibleColumns[k] === false);
      emit({ visibleColumns: newVisible, columnOrder: [...currentKeys, ...unselectedKeys] });
    } else {
      const sourceIdx = currentKeys.indexOf(sourceKey);
      if (sourceIdx !== -1) {
          currentKeys.splice(sourceIdx, 1);
          currentKeys.push(sourceKey); 
          const unselectedKeys = columnOrder.filter(k => visibleColumns[k] === false);
          emit({ columnOrder: [...currentKeys, ...unselectedKeys] });
      }
    }
    setDraggedKey(null);
  };

  const isAllAvailChecked = filteredAvailable.length > 0 && filteredAvailable.every((c) => checkedAvailable.has(c.viewKey));
  const isAllSelChecked = filteredSelected.length > 0 && filteredSelected.every((c) => checkedSelected.has(c.viewKey));

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
        <span style={{ fontSize: "14px", color: "#475569", fontWeight: "500" }}>
          {selectedColumns.length} of {TEMPLATE_COLUMNS.length} selected
        </span>
        <button type="button" className="btn btn-link btn-sm" onClick={handleReset} style={{ padding: 0, textDecoration: "none", fontWeight: 500 }}>
          Reset to Default
        </button>
      </div>

      <div style={styles.container}>
        {/* LEFT PANEL: AVAILABLE */}
        <div style={styles.panel}>
          <div style={styles.panelHeader}>
            <label style={{ display: "flex", alignItems: "center", gap: "8px", margin: 0, cursor: "pointer", fontWeight: "600" }}>
              <input type="checkbox" className="form-check-input" checked={isAllAvailChecked} onChange={handleSelectAllAvailable} />
              Available ({filteredAvailable.length})
            </label>
          </div>
          <div style={styles.searchWrap}>
            <input type="text" className="form-control form-control-sm" placeholder="Search available..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
          </div>
          <div style={styles.list} onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; }} onDrop={onDropLeftPanel}>
            {Object.keys(groupedAvailable).length === 0 ? (
              <div className="text-center text-muted" style={{ padding: "20px", fontSize: "13px" }}>No columns match.</div>
            ) : (
              Object.entries(groupedAvailable).map(([group, cols]) => {
                const isExpanded = expandedGroups[group] !== false;
                return (
                  <div key={group}>
                    <div style={styles.accordionHeader} onClick={() => toggleGroup(group)}>
                      <i className={`fas fa-chevron-${isExpanded ? "down" : "right"}`} style={{ width: "16px" }} />
                      {group} ({cols.length})
                    </div>
                    {isExpanded && (
                      <div style={styles.accordionContent}>
                        {cols.map((c) => (
                          <label
                            key={c.viewKey}
                            style={{ ...styles.row, opacity: draggedKey === c.viewKey ? 0.4 : 1 }}
                            draggable={true}
                            onDragStart={(e) => onDragStart(e, c.viewKey)}
                            onDragEnd={onDragEnd}
                            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f8fafc")}
                            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#fff")}
                          >
                            <i className="fas fa-grip-vertical" style={{ color: "#cbd5e1", marginRight: "8px", fontSize: "12px" }} />
                            <input type="checkbox" style={styles.checkbox} checked={checkedAvailable.has(c.viewKey)} onChange={() => toggleAvailableCheck(c.viewKey)} />
                            <span style={{ fontSize: "13px" }}>{c.label}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* MIDDLE ACTIONS */}
        <div style={styles.centerActions}>
          <button type="button" className="btn btn-secondary btn-sm" onClick={moveRight} disabled={checkedAvailable.size === 0}>
            <i className="fas fa-chevron-right" /> Add
          </button>
          <button type="button" className="btn btn-secondary btn-sm" onClick={moveLeft} disabled={checkedSelected.size === 0}>
            <i className="fas fa-chevron-left" /> Remove
          </button>
        </div>

        {/* RIGHT PANEL: SELECTED */}
        <div style={styles.panel}>
          <div style={styles.panelHeader}>
            <label style={{ display: "flex", alignItems: "center", gap: "8px", margin: 0, cursor: "pointer", fontWeight: "600" }}>
              <input type="checkbox" className="form-check-input" checked={isAllSelChecked} onChange={handleSelectAllSelected} />
              Selected ({filteredSelected.length})
            </label>
          </div>
          <div style={styles.searchWrap}>
             <input type="text" className="form-control form-control-sm" placeholder="Search selected..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
          </div>
          <div style={styles.list} onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; }} onDrop={onDropRightPanelEmpty}>
            {filteredSelected.length === 0 ? (
              <div style={{ padding: "20px", fontSize: "13px", textAlign: "center", border: "2px dashed #e2e8f0", borderRadius: "8px", margin: "10px" }}>
                Drag columns here or select from left.
              </div>
            ) : (
              filteredSelected.map((c, i) => {
                const isDraggingOver = dragOverKey === c.viewKey;
                
                const borderStyle = isDraggingOver 
                  ? (dropPosition === "top" ? { borderTop: "2px solid #3b82f6" } : { borderBottom: "2px solid #3b82f6" }) 
                  : {};

                return (
                  <div
                    key={c.viewKey}
                    style={{
                      ...styles.selectedRow,
                      ...borderStyle,
                      opacity: draggedKey === c.viewKey ? 0.4 : 1,
                    }}
                    draggable={true}
                    onDragStart={(e) => onDragStart(e, c.viewKey)}
                    onDragEnd={onDragEnd}
                    onDragOver={(e) => onDragOverItem(e, c.viewKey)}
                    onDrop={(e) => onDropRightItem(e, c.viewKey)}
                  >
                    <input type="checkbox" style={styles.checkbox} checked={checkedSelected.has(c.viewKey)} onChange={() => toggleSelectedCheck(c.viewKey)} />
                    <span style={{ fontSize: "12px", color: "#94a3b8", width: "24px", textAlign: "right", marginRight: "8px" }}>
                      {selectedColumns.findIndex(sc => sc.viewKey === c.viewKey) + 1}.
                    </span>
                    <input
                      type="text"
                      style={styles.labelInput}
                      value={columnLabels[c.viewKey] !== undefined ? columnLabels[c.viewKey] : c.label}
                      placeholder={c.label}
                      onChange={(e) => setLabel(c.viewKey, e.target.value)}
                      onMouseDown={(e) => e.stopPropagation()}
                      draggable={false}
                    />
                    
                    <div style={{ display: "flex", alignItems: "center", marginLeft: "8px" }}>
                      <button type="button" style={styles.iconBtn} onClick={() => moveRow(c.viewKey, "up")} disabled={i === 0} title="Move Up">
                        <i className="fas fa-chevron-up" />
                      </button>
                      <button type="button" style={styles.iconBtn} onClick={() => moveRow(c.viewKey, "down")} disabled={i === filteredSelected.length - 1} title="Move Down">
                        <i className="fas fa-chevron-down" />
                      </button>
                      <button type="button" style={{ ...styles.iconBtn, color: "#ef4444" }} onClick={() => emit({ visibleColumns: { ...visibleColumns, [c.viewKey]: false } })} title="Remove Column">
                        <i className="fas fa-times" />
                      </button>
                      
                      <div style={{ cursor: "grab", padding: "4px", color: "#94a3b8" }}>
                        <i className="fas fa-grip-vertical" />
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
}